import {
  AnalyticsContractsListRequestSchema,
  AnalyticsDashboardRequestSchema,
  AnalyticsOverviewRequestSchema,
  AnalyticsRenewalsDueRequestSchema,
  ENDPOINTS,
  type AnalyticsContractsListRequest,
  type AnalyticsContractsListResponse,
  type AnalyticsDashboardRequest,
  type AnalyticsDashboardResponse,
  type AnalyticsOverviewRequest,
  type AnalyticsOverviewResponse,
  type AnalyticsRenewalsDueRequest,
  type AnalyticsRenewalsDueResponse,
} from '@clm/shared-types';

import { apiGet, type Query } from './client';

/**
 * `GET /v1/analytics/overview` and `GET /v1/analytics/dashboard` — CLM-81.
 *
 * The two reads behind the **Analytics** section. Every query parameter is
 * optional: `period` narrows the Overview by `created_utc`, `party_key` is the
 * primary party (a narrowing filter beside the API's scope, D-81-5), `region`
 * is the primary party's region (CLM-87, the same shape one join further),
 * `cloudeq_entity` is the signing cloudEQ entity (CLM-91, a column on the
 * contract itself) and `day_start_utc` is the browser's local midnight in UTC — the "N today"
 * boundary (D-81-9). Each request is validated `.strict()` before anything is
 * sent and each response is parsed against the registry schema, so an
 * off-contract body is a `ZodError`, never a half-rendered board.
 *
 * NFR-06: no encoder here ever emits a `scope` key — scope is the API's, from
 * the token.
 */

const OVERVIEW = ENDPOINTS['analytics-overview'];
const DASHBOARD = ENDPOINTS['analytics-dashboard'];

export function analyticsOverviewQuery(request: AnalyticsOverviewRequest): Query {
  return {
    ...(request.period === undefined ? {} : { period: request.period }),
    ...(request.party_key === undefined ? {} : { party_key: request.party_key }),
    /* CLM-87 — the PRIMARY party's region, AND-ed beside party_key by the API (D-87-11). */
    ...(request.region === undefined ? {} : { region: request.region }),
    /* CLM-91 — the signing cloudEQ entity, an equality on the contract's own column beside the scope (D-91-5). */
    ...(request.cloudeq_entity === undefined ? {} : { cloudeq_entity: request.cloudeq_entity }),
    ...(request.day_start_utc === undefined ? {} : { day_start_utc: request.day_start_utc }),
  };
}

export function analyticsDashboardQuery(request: AnalyticsDashboardRequest): Query {
  return {
    ...(request.party_key === undefined ? {} : { party_key: request.party_key }),
    ...(request.region === undefined ? {} : { region: request.region }),
    ...(request.cloudeq_entity === undefined ? {} : { cloudeq_entity: request.cloudeq_entity }),
  };
}

export async function fetchAnalyticsOverview(
  request: AnalyticsOverviewRequest,
  signal?: AbortSignal,
): Promise<AnalyticsOverviewResponse> {
  const validated = AnalyticsOverviewRequestSchema.parse(request);
  const raw = await apiGet<unknown>(OVERVIEW.path, {
    query: analyticsOverviewQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return OVERVIEW.response.parse(raw);
}

export async function fetchAnalyticsDashboard(
  request: AnalyticsDashboardRequest,
  signal?: AbortSignal,
): Promise<AnalyticsDashboardResponse> {
  const validated = AnalyticsDashboardRequestSchema.parse(request);
  const raw = await apiGet<unknown>(DASHBOARD.path, {
    query: analyticsDashboardQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return DASHBOARD.response.parse(raw);
}

/* --- CLM-90 · due for renewal (D-90-3, D-90-8) ------------------------------ */

const RENEWALS = ENDPOINTS['analytics-renewals-due'];

/** Dotted `page.limit` / `page.cursor`, the shape `api/notifications.ts` uses; never a `scope` key (NFR-06). */
export function analyticsRenewalsDueQuery(request: AnalyticsRenewalsDueRequest): Query {
  const query: Record<string, string | number> = {};
  if (request.party_key !== undefined) query['party_key'] = request.party_key;
  if (request.region !== undefined) query['region'] = request.region;
  if (request.cloudeq_entity !== undefined) query['cloudeq_entity'] = request.cloudeq_entity;
  if (request.window !== undefined) query['window'] = request.window;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export async function fetchAnalyticsRenewalsDue(
  request: AnalyticsRenewalsDueRequest,
  signal?: AbortSignal,
): Promise<AnalyticsRenewalsDueResponse> {
  const validated = AnalyticsRenewalsDueRequestSchema.parse(request);
  const raw = await apiGet<unknown>(RENEWALS.path, {
    query: analyticsRenewalsDueQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return RENEWALS.response.parse(raw);
}

/* --- CLM-95 · the list behind a KPI / status-mix tile (D-95-1) ------------- */

const CONTRACTS_LIST = ENDPOINTS['analytics-contracts-list'];

/** Same dotted `page.limit` / `page.cursor` shape as the renewals query; never a `scope` key (NFR-06). */
export function analyticsContractsListQuery(request: AnalyticsContractsListRequest): Query {
  const query: Record<string, string | number | readonly string[]> = {};
  if (request.party_key !== undefined) query['party_key'] = request.party_key;
  if (request.region !== undefined) query['region'] = request.region;
  if (request.cloudeq_entity !== undefined) query['cloudeq_entity'] = request.cloudeq_entity;
  if (request.lifecycle_status !== undefined) query['lifecycle_status'] = request.lifecycle_status;
  if (request.processing_status !== undefined) query['processing_status'] = request.processing_status;
  if (request.expiring_within_days !== undefined) query['expiring_within_days'] = request.expiring_within_days;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export async function fetchAnalyticsContractsList(
  request: AnalyticsContractsListRequest,
  signal?: AbortSignal,
): Promise<AnalyticsContractsListResponse> {
  const validated = AnalyticsContractsListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(CONTRACTS_LIST.path, {
    query: analyticsContractsListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return CONTRACTS_LIST.response.parse(raw);
}
